import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import AppLayout from './components/AppLayout';
import GuestLayout from './components/GuestLayout';
import ProtectedRoute from './components/ProtectedRoute';
import { useAuth } from './context/AuthContext';

// Route-level code-splitting. Every page becomes its own chunk so the
// initial login/root navigation only downloads the shell + AntD + the one
// page the user is landing on. Was ~1.5MB single chunk before this split.
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const OrdersReport = lazy(() => import('./pages/OrdersReport'));
const ReportsContainer = lazy(() => import('./pages/ReportsContainer'));
const UniUniShipmentsReport = lazy(() => import('./pages/UniUniShipmentsReport'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const PickupAddress = lazy(() => import('./pages/PickupAddress'));
const ExportReport = lazy(() => import('./pages/ExportReport'));
const ProductsReport = lazy(() => import('./pages/ProductsReport'));
const InventoryManagement = lazy(() => import('./pages/InventoryManagement'));
const InventoryReconciliation = lazy(() => import('./pages/InventoryReconciliation'));
const Reports = lazy(() => import('./pages/Reports'));

function PageFallback() {
  return (
    <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
      <Spin size="large" />
    </div>
  );
}

function RootRedirect() {
  const { isAdmin, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Navigate to={isAdmin ? '/admin' : '/orders'} replace />;
}

export default function App() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route element={<GuestLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/reset" element={<ResetPassword />} />
        </Route>
        <Route element={<AppLayout />}>
          <Route path="/admin" element={<ProtectedRoute requireAdmin><AdminDashboard /></ProtectedRoute>} />
          <Route path="/" element={<RootRedirect />} />
          <Route path="/orders" element={<ProtectedRoute><OrdersReport /></ProtectedRoute>} />
          <Route path="/shipping" element={<ProtectedRoute><ReportsContainer /></ProtectedRoute>} />
          {/* Regular operators need this to print labels + monitor shipments. */}
          <Route path="/uniuni-shipments" element={<ProtectedRoute><UniUniShipmentsReport /></ProtectedRoute>} />
          <Route path="/order/:orderId" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
          <Route path="/pickup-addresses" element={<ProtectedRoute requireAdmin><PickupAddress /></ProtectedRoute>} />
          <Route path="/return" element={<ProtectedRoute><ReportsContainer /></ProtectedRoute>} />
          <Route path="/export" element={<ProtectedRoute requireAdmin><ExportReport /></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute><ProductsReport /></ProtectedRoute>} />
          <Route path="/inventory" element={<ProtectedRoute><InventoryManagement /></ProtectedRoute>} />
          <Route path="/inventory-recon" element={<ProtectedRoute requireAdmin><InventoryReconciliation /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute requirePermission="reports:read"><Reports /></ProtectedRoute>} />
        </Route>
      </Routes>
    </Suspense>
  );
}
